import axios from 'axios'

import { useCookieStore } from '~/store/cookieStore'
import { getAxiosConfig } from '~/utils/requests'

import type { Hex } from 'viem'
import type { TraceCall } from '~/components/txn-display/state-change/types'
import type { SupportedChainId } from '~/constants/chains'
import type { BackendTransactionCall, IAddress } from '~/types'

// One frame of a simulated call tree. traceAddress is the frame's position
// in that tree, e.g. [0, 2] for the third sub-call of the outer call.
export type SimulatedCallTraceItem = {
  callType: string
  from: string
  to: string
  value?: string
  error?: string
  traceAddress?: number[]
}

export type SimulatedLog = {
  address: string
  topics: string[]
  data: string
}

// Allowance read after the simulated calls for each Approval log, with the
// amount as a decimal string.
export type SimulatedAllowance = {
  owner: string
  spender: string
  token: string
  amount: string
}

export type CallSimulationResult = {
  simulationId?: string
  errorMessage?: string
  logs: SimulatedLog[]
  callTrace: SimulatedCallTraceItem[]
  allowances?: SimulatedAllowance[]
}

export type SimulateCallsArgs = {
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
  // A persisted transaction is simulated by id from its stored calls, which
  // the server allows without a session so public transaction pages keep
  // their preview. A draft sends its calls and needs org access.
  transactionId?: string
  save?: boolean
}

// Simulation errors render inline, so this bypasses useServerRequests and
// its toasts; a failed request rejects like the vendor call it replaces.
const postToServer = async <T>(path: string, params: unknown): Promise<T> => {
  const authToken = useCookieStore.getState().authToken
  const config = getAxiosConfig()
  const response = await axios.post<T>(path, params, {
    ...config,
    headers: {
      ...config.headers,
      ...(authToken ? { Authorization: authToken } : {}),
    },
  })
  return response.data
}

export const simulateCallsOnServer = ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
  save,
}: SimulateCallsArgs) =>
  postToServer<CallSimulationResult>(
    '/api/smart_accounts/transactions/simulation',
    transactionId
      ? { kind: 'transaction', transactionId, save }
      : {
          kind: 'draft',
          chainId,
          smartAccountAddress,
          calls: calls.map((call) => ({
            to: call.to,
            value: call.value.toString(),
            data: call.data,
          })),
          save,
        },
  )

// Resolves undefined on any failure: the trace only adds native transfers,
// and the caller shows the receipt logs without it.
export const traceTransactionOnServer = async ({
  chainId,
  transactionHash,
}: {
  chainId: number
  transactionHash: Hex
}): Promise<TraceCall | undefined> => {
  try {
    const { trace } = await postToServer<{ trace?: TraceCall }>(
      '/api/smart_accounts/transactions/simulation/trace',
      { chainId, transactionHash },
    )
    return trace
  } catch {
    return undefined
  }
}
